import classNames from 'classnames';
import React from 'react';
import { removeWhiteSpaceChars } from '../../../Model/helpers';
import { ITableDataProps, ITableHeadingProps, ITableRowProps, ITableSectionProps } from '../../../Model/Table/SummaryTable';

interface IProps {
    data: ITableDataProps;
    onSegmentClick?: (label: string) => void;
    nestedRows?: boolean;
}


export const SummaryTable: React.FC<IProps> = (props: IProps) => {

    const onHeadCellClick = (headingProps: ITableHeadingProps) => () => {
        var finalLabel = removeWhiteSpaceChars(headingProps.attribute);
        if (props.onSegmentClick) props.onSegmentClick(finalLabel);
    }

    const generateHead = (headDatas: Array<ITableHeadingProps>) => {
        const head = headDatas.map((headData, idx) => {
            const clickable = idx !== 0 && idx !== (headDatas.length - 1);
            return (
                <th
                    key={idx}
                    className={classNames(headData.thClass, { clickable })}
                    onClick={clickable ? onHeadCellClick(headData) : () => null}
                >{headData.heading}</th>
            )
        }
        );
        return (
            <thead>
                <tr>
                    {head}
                </tr>
            </thead>
        );
    }

    const onCellClick = (columnData: ITableRowProps, headData: ITableHeadingProps, rowLabel: string) => () => {
        let label = columnData.Label !== undefined ? columnData.Label : columnData.label
        var finalLabel = headData.attribute + '|' + label + "|" + rowLabel;
        finalLabel = removeWhiteSpaceChars(finalLabel);
        if (props.onSegmentClick) props.onSegmentClick(finalLabel);
    }

    const generateNestedRowColumns = (columnData: ITableSectionProps, headDatas: Array<ITableHeadingProps>, index: number, style: string) => {
            const columns = headDatas.map((headData, idx) => {
                const nestedRows = columnData.rowData.map((row, index) => {
                    const columnValueFromAttr = (row as any)[headData.attribute];
                    const columnCellClassFromAttr = row[`${headData.attribute}CellClass`];
                    const isClickable = headData.attribute === 'label' ? false : !row.disableClick;
                    const columnStyles = row.tdClass == null ? Array(headDatas.length).fill('') : row.tdClass;
                    const rowLabel = columnData.rowLabel !== undefined ? columnData.rowLabel : "";
                    return (
                        <tr>
                            <td
                                key={idx}
                                className={classNames(headData.thClass ,columnStyles[index], columnCellClassFromAttr, { 'clickable': isClickable })}
                                onClick={isClickable ? onCellClick(row, headData, rowLabel) : undefined}
                            >
                                <span>{columnValueFromAttr}</span>
                            </td>
                        </tr>
                    );
                
                });
                return (
                    <> 
                        <td style={{ width: '100%', height: '100%', padding: '0', border:'0px'}}>
                            <table className='nestedTable'>

                                {nestedRows}

                            </table>
                        </td>
                    </>

                );
            });
        return (
            <>
            <tr className={classNames(columnData.trClass ?? '', style)} key={index} >
                <td
                    key={index}
                    className={classNames( 'rowLabel', { 'clickable': false })}
                >
                    <span>{columnData.rowLabel}</span>
                </td>
                {columns}
            </tr>
            </>
        );
    }

    const generateColumns = (columnData: ITableRowProps, headDatas: Array<ITableHeadingProps>, index: number, style: string) => {
        const columnStyles = columnData.tdClass == null ? Array(headDatas.length).fill('') : columnData.tdClass;
        const columns = headDatas.map((headData, idx) => {
            const columnValueFromAttr = (columnData as any)[headData.attribute];
            const columnCellClassFromAttr = columnData[`${headData.attribute}CellClass`];
            const isClickable = !columnData.disableClick;
            const rowLabel = columnData.rowLabel !== undefined ? columnData.rowLabel : "";
            return (
                <td
                    key={idx}
                    className={classNames(headData.thClass, columnStyles[idx], columnCellClassFromAttr, { 'clickable': isClickable })}
                    onClick={isClickable ? onCellClick(columnData, headData, rowLabel) : undefined}
                >
                    <span>{columnValueFromAttr}</span>
                </td>
            );

        });
        return (
            <tr className={(columnData.trClass ?? '') + ' ' + style} key={index} >
                {columns}
            </tr>
        );
    }

    const generateBody = (bodyData: Array<ITableSectionProps>, headData: Array<ITableHeadingProps>) => {
        let rows: any[] = [];
        if(props.nestedRows !== undefined && props.nestedRows === true){
            rows = bodyData.map((data, index) =>
                generateNestedRowColumns(data, headData.slice(1), index, '')
            );
        }
        else{
            rows = bodyData.map((data, index) =>
                generateColumns(data.rowData[0], headData, index, '')
            );
        }

        return (
            <tbody>
                {rows}
            </tbody>
        );
    }

    const generateFoot = (footData: Array<ITableSectionProps>, headData: Array<ITableHeadingProps>, footclass?: string) => {
        let rows: any[] = [];
        if(props.nestedRows !== undefined && props.nestedRows === true){
            rows = footData.map((data, index) =>
                generateNestedRowColumns(data, headData.slice(1), index, 'totalText')
            );
        }
        else{
            rows = footData.map((data, index) =>
                generateColumns(data.rowData[0], headData, index, 'totalText')
            );
        }
        return (
            <tfoot className={footclass ?? ''}>
                {rows}
            </tfoot>
        );
    }

    const generateSeperatedRows = (seperatedData: Array<ITableSectionProps>, headData: Array<ITableHeadingProps>, footclass?: string) => {
        const rows = seperatedData.map((data, index) =>
            generateColumns(data, headData, index, 'totalText')
        );
        let x = rows.map((row, index) => {
            return (
                <table className='table table-bordered' key={index}>
                    <tbody>
                        {row}
                    </tbody>
                </table>
            );
        });
        return x;
    }

    const generateTable = (tableData: ITableDataProps) => {
        if (tableData == null) return null;
        const head = tableData.head.length > 0 ? generateHead(tableData.head) : null;
        const body = tableData.body.length > 0 ? generateBody(tableData.body, tableData.head) : null;
        const foot = tableData.foot.length > 0 ? generateFoot(tableData.foot, tableData.head, tableData.footClass) : null;

        let hasSeperatedRows = tableData?.seperated;
        const seperatedData = tableData?.seperated ?? new Array<ITableSectionProps>();
        const seperated = hasSeperatedRows ? generateSeperatedRows(seperatedData, tableData.head, tableData.footClass) : null;

        return (
            <>
                <table className='table table-bordered'>
                    {head}
                    {body}
                    {foot}
                </table>
                {seperated}
            </>
        );
    }

    return (
        <div className="table-responsive">
            {generateTable(props.data)}
        </div>
    );
}